import {
  lineCells,
  type Cell,
  type PixelDocument,
  type Rgba,
  type ToolId,
} from "../core/index.js";

export interface CanvasViewCallbacks {
  readonly onStrokeBegin: () => void;
  readonly onStrokeCommit: (toolLabel: string) => void;
  readonly onHoverCell: (cell: { x: number; y: number } | null) => void;
  readonly onSampleColor: (color: Rgba) => void;
  readonly onZoomChange: (zoom: number) => void;
}

export class CanvasView {
  private readonly viewport: HTMLElement;
  private readonly stage: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly callbacks: CanvasViewCallbacks;

  private doc: PixelDocument;
  private activeColor: Rgba;
  private currentTool: ToolId = "pencil";

  private zoom = 24;
  private panX = 0;
  private panY = 0;
  private gridEnabled = true;

  private hoverCell: { x: number; y: number } | null = null;
  private isStrokeActive = false;
  private isErasing = false;
  private lastCell: Cell | null = null;

  private isPanning = false;
  private activePointerId: number | null = null;
  private activeInteraction: "paint" | "pan" | null = null;
  private panStartX = 0;
  private panStartY = 0;
  private isSpacePressed = false;

  private floatingControls!: HTMLElement;
  private zoomPercentLabel!: HTMLButtonElement;
  private gridToggleBtn!: HTMLButtonElement;

  constructor(
    container: HTMLElement,
    doc: PixelDocument,
    initialColor: Rgba,
    callbacks: CanvasViewCallbacks,
  ) {
    this.viewport = container;
    this.doc = doc;
    this.activeColor = initialColor;
    this.callbacks = callbacks;

    this.viewport.className = "canvas-viewport";
    this.viewport.tabIndex = 0;

    this.stage = document.createElement("div");
    this.stage.className = "canvas-stage";

    this.canvas = document.createElement("canvas");
    this.canvas.className = "pixel-canvas";
    const ctx = this.canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      throw new Error("2D-Rendering-Kontext für die Leinwand konnte nicht initialisiert werden.");
    }
    this.ctx = ctx;

    this.stage.appendChild(this.canvas);
    this.viewport.appendChild(this.stage);

    this.createFloatingControls();
    this.bindEvents();

    // Initial setup
    requestAnimationFrame(() => {
      this.fitToViewport();
    });
  }

  private createFloatingControls(): void {
    this.floatingControls = document.createElement("div");
    this.floatingControls.className = "canvas-floating-controls";

    const zoomOutBtn = document.createElement("button");
    zoomOutBtn.type = "button";
    zoomOutBtn.className = "floating-btn";
    zoomOutBtn.title = "Verkleinern (-)";
    zoomOutBtn.setAttribute("aria-label", "Verkleinern");
    zoomOutBtn.textContent = "−";
    zoomOutBtn.addEventListener("click", () => this.zoomOut());

    this.zoomPercentLabel = document.createElement("button");
    this.zoomPercentLabel.type = "button";
    this.zoomPercentLabel.className = "floating-btn floating-btn-text";
    this.zoomPercentLabel.title = "Auf 100% (1:1) zurücksetzen";
    this.zoomPercentLabel.setAttribute("aria-label", "Zoom auf 100% zurücksetzen");
    this.zoomPercentLabel.textContent = `${this.zoom * 100}%`;
    this.zoomPercentLabel.addEventListener("click", () => this.setZoom(1));

    const zoomInBtn = document.createElement("button");
    zoomInBtn.type = "button";
    zoomInBtn.className = "floating-btn";
    zoomInBtn.title = "Vergrößern (+)";
    zoomInBtn.setAttribute("aria-label", "Vergrößern");
    zoomInBtn.textContent = "+";
    zoomInBtn.addEventListener("click", () => this.zoomIn());

    const fitBtn = document.createElement("button");
    fitBtn.type = "button";
    fitBtn.className = "floating-btn floating-btn-fit";
    fitBtn.title = "An Fenster anpassen (0)";
    fitBtn.setAttribute("aria-label", "An Fenster anpassen");
    fitBtn.textContent = "⛶ Einpassen";
    fitBtn.addEventListener("click", () => this.fitToViewport());

    this.gridToggleBtn = document.createElement("button");
    this.gridToggleBtn.type = "button";
    this.gridToggleBtn.className = "floating-btn floating-btn-grid is-active";
    this.gridToggleBtn.title = "Pixelraster ein-/ausblenden";
    this.gridToggleBtn.setAttribute("aria-label", "Pixelraster umschalten");
    this.gridToggleBtn.textContent = "▦ Raster";
    this.gridToggleBtn.addEventListener("click", () => {
      this.gridEnabled = !this.gridEnabled;
      this.gridToggleBtn.classList.toggle("is-active", this.gridEnabled);
      this.render();
    });

    this.floatingControls.appendChild(zoomOutBtn);
    this.floatingControls.appendChild(this.zoomPercentLabel);
    this.floatingControls.appendChild(zoomInBtn);
    this.floatingControls.appendChild(fitBtn);
    this.floatingControls.appendChild(this.gridToggleBtn);
    this.viewport.appendChild(this.floatingControls);
  }

  private bindEvents(): void {
    // Prevent context menu on canvas (allows right-click erasing!)
    this.viewport.addEventListener("contextmenu", (e) => {
      e.preventDefault();
    });

    // Pointer events on canvas
    this.canvas.addEventListener("pointerdown", (e) => this.onPointerDown(e));
    this.canvas.addEventListener("pointermove", (e) => this.onPointerMove(e));
    this.canvas.addEventListener("pointerup", (e) => this.onPointerUp(e));
    this.canvas.addEventListener("pointercancel", (e) => this.onPointerUp(e));
    this.canvas.addEventListener("lostpointercapture", (e) => this.onLostPointerCapture(e));
    this.canvas.addEventListener("pointerleave", () => {
      if (this.activeInteraction === null) {
        this.hoverCell = null;
        this.callbacks.onHoverCell(null);
        this.render();
      }
    });

    // Wheel zoom on viewport
    this.viewport.addEventListener("wheel", (e) => this.onWheel(e), { passive: false });

    // Keyboard space pan
    window.addEventListener("keydown", (e) => {
      if (e.code === "Space" && !this.isSpacePressed && !this.isInputFocused()) {
        this.isSpacePressed = true;
        this.updateCursor();
      }
    });

    window.addEventListener("keyup", (e) => {
      if (e.code === "Space") {
        this.isSpacePressed = false;
        this.updateCursor();
      }
    });

    // Handle viewport resize
    const resizeObserver = new ResizeObserver(() => {
      this.updateTransform();
    });
    resizeObserver.observe(this.viewport);
  }

  private isInputFocused(): boolean {
    const active = document.activeElement;
    if (!active) return false;
    const tag = active.tagName.toUpperCase();
    return tag === "INPUT" || tag === "TEXTAREA" || (active as HTMLElement).isContentEditable;
  }

  private updateCursor(): void {
    if (this.isPanning) {
      this.viewport.style.cursor = "grabbing";
      this.canvas.style.cursor = "grabbing";
    } else if (this.isSpacePressed) {
      this.viewport.style.cursor = "grab";
      this.canvas.style.cursor = "grab";
    } else if (this.currentTool === "eyedropper") {
      this.viewport.style.cursor = "default";
      this.canvas.style.cursor = "crosshair";
    } else {
      this.viewport.style.cursor = "default";
      this.canvas.style.cursor = "crosshair";
    }
  }

  private onPointerDown(e: PointerEvent): void {
    if (this.activePointerId !== null) return;
    // Middle button or Space+Left triggers pan
    if (e.button === 1 || (e.button === 0 && this.isSpacePressed)) {
      this.activePointerId = e.pointerId;
      this.activeInteraction = "pan";
      this.isPanning = true;
      this.panStartX = e.clientX - this.panX;
      this.panStartY = e.clientY - this.panY;
      this.canvas.setPointerCapture(e.pointerId);
      this.updateCursor();
      return;
    }

    // Only handle primary (left) or secondary (right) clicks
    if (e.button !== 0 && e.button !== 2) return;

    const cell = this.getCellAtPointer(e);
    if (!cell) return;

    // Eyedropper tool
    if (this.currentTool === "eyedropper" && e.button === 0) {
      const pixel = this.doc.getPixel(cell.x, cell.y);
      this.callbacks.onSampleColor(pixel);
      return;
    }

    // Drawing / Erasing stroke
    this.activePointerId = e.pointerId;
    this.activeInteraction = "paint";
    this.canvas.setPointerCapture(e.pointerId);
    this.isStrokeActive = true;
    this.isErasing = e.button === 2 || this.currentTool === "eraser";
    this.lastCell = cell;

    this.callbacks.onStrokeBegin();
    this.applyCell(cell, this.isErasing);
    this.render();
  }

  private onPointerMove(e: PointerEvent): void {
    if (this.activePointerId !== null && e.pointerId !== this.activePointerId) return;
    if (this.isPanning) {
      this.panX = e.clientX - this.panStartX;
      this.panY = e.clientY - this.panStartY;
      this.updateTransform();
      return;
    }

    const cell = this.getCellAtPointer(e);
    const hasCellChanged =
      (!this.hoverCell && cell) ||
      (this.hoverCell && !cell) ||
      (this.hoverCell && cell && (this.hoverCell.x !== cell.x || this.hoverCell.y !== cell.y));

    if (hasCellChanged) {
      this.hoverCell = cell;
      this.callbacks.onHoverCell(cell);
    }

    if (this.isStrokeActive && this.lastCell) {
      // Calculate continuous line to pointer position (clamped to document bounds)
      const rect = this.canvas.getBoundingClientRect();
      const rawX = Math.floor(((e.clientX - rect.left) / rect.width) * this.doc.width);
      const rawY = Math.floor(((e.clientY - rect.top) / rect.height) * this.doc.height);
      const clampedX = Math.max(0, Math.min(this.doc.width - 1, rawX));
      const clampedY = Math.max(0, Math.min(this.doc.height - 1, rawY));
      const currentCell: Cell = { x: clampedX, y: clampedY };

      if (currentCell.x !== this.lastCell.x || currentCell.y !== this.lastCell.y) {
        const line = lineCells(this.lastCell, currentCell);
        for (const pt of line) {
          if (pt.x >= 0 && pt.x < this.doc.width && pt.y >= 0 && pt.y < this.doc.height) {
            this.applyCell(pt, this.isErasing);
          }
        }
        this.lastCell = currentCell;
        this.render();
      }
    } else if (hasCellChanged) {
      this.render();
    }
  }

  private onPointerUp(e: PointerEvent): void {
    if (this.activePointerId !== e.pointerId) return;
    this.finishInteraction();
  }

  private onLostPointerCapture(e: Event): void {
    const pointerId = (e as PointerEvent).pointerId;
    if (this.activePointerId === pointerId) this.finishInteraction();
  }

  /** Finish the current pointer interaction once. Safe to call repeatedly. */
  public finishInteraction(): void {
    this.endInteraction(true);
  }

  /** Cancel the current pointer interaction without committing a paint stroke. */
  public cancelInteraction(): void {
    this.endInteraction(false);
  }

  private endInteraction(commitPaint: boolean): void {
    if (this.activeInteraction === null) return;
    const pointerId = this.activePointerId;
    const wasPainting = this.activeInteraction === "paint" && this.isStrokeActive;
    const label = this.isErasing ? "Radierer" : "Stift";

    this.activePointerId = null;
    this.activeInteraction = null;
    this.isPanning = false;
    this.isStrokeActive = false;
    this.lastCell = null;
    this.isErasing = false;
    if (pointerId !== null) {
      try {
        if (this.canvas.hasPointerCapture(pointerId)) this.canvas.releasePointerCapture(pointerId);
      } catch {
        // Capture may already have been lost.
      }
    }
    this.updateCursor();
    if (wasPainting && commitPaint) this.callbacks.onStrokeCommit(label);
    this.render();
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    const zoomFactor = e.deltaY < 0 ? 1 : -1;
    const step = this.zoom >= 16 ? 4 : this.zoom >= 8 ? 2 : 1;
    const oldZoom = this.zoom;
    const newZoom = Math.max(1, Math.min(64, this.zoom + zoomFactor * step));

    if (newZoom !== oldZoom) {
      const rect = this.viewport.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Calculate canvas point under cursor before zoom
      const canvasX = (mouseX - this.panX) / oldZoom;
      const canvasY = (mouseY - this.panY) / oldZoom;

      this.zoom = newZoom;
      this.panX = Math.round(mouseX - canvasX * newZoom);
      this.panY = Math.round(mouseY - canvasY * newZoom);

      this.updateTransform();
      this.render();
      this.callbacks.onZoomChange(this.zoom);
    }
  }

  private applyCell(cell: { x: number; y: number }, erase: boolean): void {
    if (erase) {
      this.doc.clearPixel(cell.x, cell.y);
    } else {
      this.doc.setPixel(cell.x, cell.y, this.activeColor);
    }
  }

  private getCellAtPointer(e: PointerEvent): { x: number; y: number } | null {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const rawX = Math.floor(((e.clientX - rect.left) / rect.width) * this.doc.width);
    const rawY = Math.floor(((e.clientY - rect.top) / rect.height) * this.doc.height);
    if (rawX < 0 || rawX >= this.doc.width || rawY < 0 || rawY >= this.doc.height) {
      return null;
    }
    return { x: rawX, y: rawY };
  }

  public render(): void {
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = this.doc.width * this.zoom;
    const cssHeight = this.doc.height * this.zoom;

    const bufferWidth = Math.round(cssWidth * dpr);
    const bufferHeight = Math.round(cssHeight * dpr);

    const maxBackingDimension = 4096;
    const maxBackingPixels = 16_777_216;
    const dimensionScale = Math.min(1, maxBackingDimension / Math.max(bufferWidth, bufferHeight));
    const pixelScale = Math.min(1, Math.sqrt(maxBackingPixels / Math.max(1, bufferWidth * bufferHeight)));
    const backingScale = Math.min(dimensionScale, pixelScale);
    const safeWidth = Math.max(1, Math.floor(bufferWidth * backingScale));
    const safeHeight = Math.max(1, Math.floor(bufferHeight * backingScale));

    // CSS dimensions remain the workspace dimensions even when the bitmap is capped.
    this.canvas.style.width = `${cssWidth}px`;
    this.canvas.style.height = `${cssHeight}px`;
    if (this.canvas.width !== safeWidth || this.canvas.height !== safeHeight) {
      this.canvas.width = safeWidth;
      this.canvas.height = safeHeight;
    }

    const scaleX = safeWidth / cssWidth;
    const scaleY = safeHeight / cssHeight;
    this.ctx.setTransform(scaleX, 0, 0, scaleY, 0, 0);
    this.ctx.imageSmoothingEnabled = false;

    const z = this.zoom;

    // 1. Draw Checkerboard background for transparent pixels
    for (let y = 0; y < this.doc.height; y++) {
      for (let x = 0; x < this.doc.width; x++) {
        const rx = Math.round(x * z);
        const ry = Math.round(y * z);
        const rw = Math.round((x + 1) * z) - rx;
        const rh = Math.round((y + 1) * z) - ry;

        // Alternating subtle checker pattern (#f4f1e8 & #e4e2db)
        this.ctx.fillStyle = (x + y) % 2 === 0 ? "#f4f1e8" : "#e4e2db";
        this.ctx.fillRect(rx, ry, rw, rh);

        // 2. Draw Pixel RGBA content
        if (!this.doc.isTransparent(x, y)) {
          const p = this.doc.getPixel(x, y);
          this.ctx.fillStyle = `rgba(${p.r}, ${p.g}, ${p.b}, ${p.a / 255})`;
          this.ctx.fillRect(rx, ry, rw, rh);
        }
      }
    }

    // 3. Grid lines (when enabled and zoom is large enough)
    if (this.gridEnabled && z >= 6) {
      this.ctx.strokeStyle = "rgba(20, 28, 42, 0.22)";
      this.ctx.lineWidth = 1;
      this.ctx.beginPath();
      for (let x = 0; x <= this.doc.width; x++) {
        const gx = Math.round(x * z) + 0.5;
        this.ctx.moveTo(gx, 0);
        this.ctx.lineTo(gx, cssHeight);
      }
      for (let y = 0; y <= this.doc.height; y++) {
        const gy = Math.round(y * z) + 0.5;
        this.ctx.moveTo(0, gy);
        this.ctx.lineTo(cssWidth, gy);
      }
      this.ctx.stroke();
    }

    // 4. Outer border
    this.ctx.strokeStyle = "#798397";
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(0, 0, cssWidth, cssHeight);

    // 5. Hover Cell Indicator
    if (this.hoverCell && !this.isPanning) {
      const hx = Math.round(this.hoverCell.x * z);
      const hy = Math.round(this.hoverCell.y * z);
      const hw = Math.round((this.hoverCell.x + 1) * z) - hx;
      const hh = Math.round((this.hoverCell.y + 1) * z) - hy;

      this.ctx.strokeStyle = "#ffb454";
      this.ctx.lineWidth = Math.max(1.5, Math.min(3, z / 8));
      this.ctx.strokeRect(hx + 0.5, hy + 0.5, hw - 1, hh - 1);
    }
  }

  private updateTransform(): void {
    this.stage.style.transform = `translate(${this.panX}px, ${this.panY}px)`;
    this.zoomPercentLabel.textContent = `${this.zoom * 100}%`;
  }

  public setZoom(zoomFactor: number): void {
    const clamped = Math.max(1, Math.min(64, Math.round(zoomFactor)));
    if (clamped === this.zoom) return;

    const viewportW = this.viewport.clientWidth;
    const viewportH = this.viewport.clientHeight;
    const centerX = viewportW / 2;
    const centerY = viewportH / 2;

    const canvasCenterX = (centerX - this.panX) / this.zoom;
    const canvasCenterY = (centerY - this.panY) / this.zoom;

    this.zoom = clamped;
    this.panX = Math.round(centerX - canvasCenterX * clamped);
    this.panY = Math.round(centerY - canvasCenterY * clamped);

    this.updateTransform();
    this.render();
    this.callbacks.onZoomChange(this.zoom);
  }

  public zoomIn(): void {
    const step = this.zoom >= 16 ? 4 : this.zoom >= 8 ? 2 : 1;
    this.setZoom(this.zoom + step);
  }

  public zoomOut(): void {
    const step = this.zoom > 16 ? 4 : this.zoom > 8 ? 2 : 1;
    this.setZoom(this.zoom - step);
  }

  public fitToViewport(): void {
    const padding = 64;
    const availW = Math.max(100, this.viewport.clientWidth - padding);
    const availH = Math.max(100, this.viewport.clientHeight - padding);

    const fitZoom = Math.floor(Math.min(availW / this.doc.width, availH / this.doc.height));
    this.zoom = Math.max(1, Math.min(48, fitZoom));

    this.panX = Math.round((this.viewport.clientWidth - this.doc.width * this.zoom) / 2);
    this.panY = Math.round((this.viewport.clientHeight - this.doc.height * this.zoom) / 2);

    this.updateTransform();
    this.render();
    this.callbacks.onZoomChange(this.zoom);
  }

  public setDocument(doc: PixelDocument): void {
    this.doc = doc;
    this.fitToViewport();
  }

  public setColor(color: Rgba): void {
    this.activeColor = color;
  }

  public setTool(tool: ToolId): void {
    this.currentTool = tool;
    this.updateCursor();
  }

  public getZoom(): number {
    return this.zoom;
  }
}
